import {
  Button,
  Divider,
  Stack,
  Text,
  TextInput,
  Title,
  createPolymorphicComponent,
  type ButtonProps,
  type TextInputProps,
} from "@mantine/core";
import { forwardRef, type ReactNode } from "react";

import classes from "./AuthShell.module.css";
import { Logo } from "./Logo";

export interface AuthShellProps {
  /** The card's one h1. */
  title: string;
  children: ReactNode;
}

/**
 * The sign-in card of the live Created.ai app (design-system.md 3.1): the logo over a 398px card
 * on the page gradient. Sign-in, Google's callback and the invitation link all render inside it,
 * so it lives in ui/, where every feature may import it.
 */
export function AuthShell({ title, children }: AuthShellProps) {
  return (
    <div className={classes.page}>
      <Stack align="center" gap={24}>
        <Logo width={190} height={25} />
        <section className={classes.card}>
          <Stack gap={28}>
            <Title order={1} className={classes.title}>
              {title}
            </Title>
            {children}
          </Stack>
        </section>
      </Stack>
    </div>
  );
}

/** A pill field: a 12px Inter label over a 36px borderless input on the field background. */
export function AuthField(props: Omit<TextInputProps, "classNames">) {
  return (
    <TextInput
      radius={999}
      {...props}
      classNames={{
        label: classes.fieldLabel,
        wrapper: classes.fieldWrapper,
        input: classes.fieldInput,
      }}
    />
  );
}

export interface AuthButtonProps extends ButtonProps {
  children: ReactNode;
}

const join = (...names: Array<string | undefined>) => names.filter(Boolean).join(" ");

// The three pills share one geometry (40px or 36px tall, 999 radius, 12px sides, 14px/500), set
// here and nowhere else. Polymorphic, so a screen renders one as a router Link through
// `component` or `renderRoot`.
const Primary = forwardRef<HTMLButtonElement, AuthButtonProps>(({ className, ...props }, ref) => (
  <Button
    ref={ref}
    variant="filled"
    fullWidth
    radius={999}
    h={40}
    px={12}
    className={join(classes.primary, className)}
    {...props}
  />
));
Primary.displayName = "AuthPrimaryButton";

/** The 40px pill on the auth gradient: the card's one main action. */
export const AuthPrimaryButton = createPolymorphicComponent<"button", AuthButtonProps>(Primary);

const Default = forwardRef<HTMLButtonElement, AuthButtonProps>((props, ref) => (
  <Button
    ref={ref}
    variant="default"
    fullWidth
    radius={999}
    h={40}
    px={12}
    fz={14}
    fw={500}
    {...props}
  />
));
Default.displayName = "AuthDefaultButton";

/**
 * The 40px bordered pill: light with a border, filled dark on the dark scheme, which is what
 * Google's own sign-in button looks like on each scheme.
 */
export const AuthDefaultButton = createPolymorphicComponent<"button", AuthButtonProps>(Default);

const Secondary = forwardRef<HTMLButtonElement, AuthButtonProps>(
  ({ className, ...props }, ref) => (
    <Button
      ref={ref}
      variant="transparent"
      fullWidth
      radius={999}
      h={36}
      px={12}
      className={join(classes.secondary, className)}
      {...props}
    />
  ),
);
Secondary.displayName = "AuthSecondaryButton";

/** The 36px quiet pill: the way back, or the other way in. */
export const AuthSecondaryButton = createPolymorphicComponent<"button", AuthButtonProps>(
  Secondary,
);

/** A divider with a 12px label through it, such as "or log in with email". */
export function AuthDivider({ label }: { label: string }) {
  return (
    <Divider
      label={label}
      labelPosition="center"
      color="var(--cai-color-auth-divider)"
      classNames={{ label: classes.dividerLabel }}
    />
  );
}

/** The card's last block: a divider and a centered 14px line, with links in the primary color. */
export function AuthFooter({ children }: { children: ReactNode }) {
  return (
    <Stack gap={28}>
      <Divider color="var(--cai-color-auth-divider)" />
      <Text className={classes.hint}>{children}</Text>
    </Stack>
  );
}

/** A 12px muted line, such as the terms notice, with links in the primary color. */
export function AuthNote({ children }: { children: ReactNode }) {
  return <Text className={classes.small}>{children}</Text>;
}
